// pages/promotions/Promotions.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Divider,
  Grid,
  Skeleton,
  Button,
  IconButton,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PromotionHero from "./PromotionHero";
import axios from "axios";
import { getAuthCredentials } from "../../utils/auth";

const StatusDot = ({ active = false }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <FiberManualRecordIcon
      fontSize="small"
      sx={{ color: active ? "success.main" : "error.main" }}
    />
    <Typography variant="caption" color="text.secondary">
      {active ? "Active" : "Inactive"}
    </Typography>
  </Stack>
);

const Promotions = () => {
  const [loadingHome, setLoadingHome] = useState(true);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [homeMedia, setHomeMedia] = useState([]);
  const [logo, setLogo] = useState(null);
  const [banner, setBanner] = useState(null);

  const [slideIdx, setSlideIdx] = useState(0);
  const timerRef = useRef(null);

  const fetchHome = useCallback(async () => {
    try {
      setLoadingHome(true);
      const { token } = await getAuthCredentials();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/fetch-home`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const media = Array.isArray(res.data.media) ? res.data.media : [];
      const sorted = media
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setHomeMedia(sorted);
      setSlideIdx(0);
    } catch {
      setHomeMedia([]);
      setSlideIdx(0);
    } finally {
      setLoadingHome(false);
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      setLoadingBrands(true);
      const { token } = await getAuthCredentials();
      const [logoRes, bannerRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/fetch-logo`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/fetch-banner`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);
      setLogo(logoRes.data?.logo || null);
      setBanner(bannerRes.data?.banner || null);
    } catch {
      setLogo(null);
      setBanner(null);
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  useEffect(() => {
    fetchHome();
    fetchBrands();
  }, [fetchHome, fetchBrands]);

  const startAuto = useCallback(() => {
    if (!homeMedia.length) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSlideIdx((i) => (i + 1) % homeMedia.length);
    }, 5000);
  }, [homeMedia.length]);

  useEffect(() => {
    if (!homeMedia.length) return;
    startAuto();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [homeMedia, startAuto]);

  const prevSlide = () => {
    if (!homeMedia.length) return;
    setSlideIdx((i) => (i - 1 + homeMedia.length) % homeMedia.length);
    startAuto();
  };

  const nextSlide = () => {
    if (!homeMedia.length) return;
    setSlideIdx((i) => (i + 1) % homeMedia.length);
    startAuto();
  };

  const jumpTo = (i) => {
    if (i === slideIdx) return;
    setSlideIdx(i);
    startAuto();
  };

  return (
    <Box sx={{ p: 3 }}>
      <PromotionHero title="Promotions" />

      <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box
          sx={{ px: 2, pt: 2, display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <CampaignIcon fontSize="large" />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Promotions Module
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overview, manage homepage media, and brand assets.
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mt: 2 }} />

        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Current Home Page
              </Typography>
              <Button variant="outlined" href="/promotions/home">
                Manage Home Page
              </Button>
            </Stack>

            {loadingHome ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: 420,
                  mx: "auto",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ width: "100%", aspectRatio: "9 / 16" }}>
                  <Skeleton
                    variant="rounded"
                    sx={{ width: "100%", height: "100%" }}
                  />
                </Box>
              </Box>
            ) : homeMedia.length === 0 ? (
              <Stack
                alignItems="center"
                sx={{ py: 6, color: "text.secondary" }}
              >
                <WallpaperIcon />
                <Typography variant="body2">Nothing found</Typography>
              </Stack>
            ) : (
              <Box sx={{ display: "grid", placeItems: "center" }}>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 420,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "9 / 16",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        width: `${homeMedia.length * 100}%`,
                        height: "100%",
                        transition: "transform .5s ease",
                        transform: `translateX(-${
                          slideIdx * (100 / homeMedia.length)
                        }%)`,
                      }}
                    >
                      {homeMedia.map((m, i) => (
                        <Box
                          key={`${m.url}-${i}`}
                          sx={{
                            width: `${100 / homeMedia.length}%`,
                            height: "100%",
                            flex: "0 0 auto",
                            display: "grid",
                            placeItems: "center",
                            bgcolor: m.type === "video" ? "#000" : "#fff",
                          }}
                        >
                          {m.type === "video" ? (
                            <Box
                              component="video"
                              src={m.url}
                              muted
                              loop
                              playsInline
                              autoPlay={i === slideIdx}
                              controls={false}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={m.url}
                              alt={m.alt || "media"}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                              }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>

                    <IconButton
                      onClick={prevSlide}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: 8,
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(0,0,0,0.4)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                      }}
                      size="small"
                    >
                      <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      onClick={nextSlide}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: 8,
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(0,0,0,0.4)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                      }}
                      size="small"
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>

                    {(homeMedia[slideIdx]?.title ||
                      homeMedia[slideIdx]?.alt) && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: "rgba(0,0,0,0.45)",
                          color: "#fff",
                          px: 1,
                          py: 0.5,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          title={
                            homeMedia[slideIdx].title || homeMedia[slideIdx].alt
                          }
                          sx={{ display: "block" }}
                        >
                          {homeMedia[slideIdx].title || homeMedia[slideIdx].alt}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                  {homeMedia.map((_, i) => (
                    <Box
                      key={i}
                      onClick={() => jumpTo(i)}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: i === slideIdx ? "primary.main" : "divider",
                        cursor: "pointer",
                        transition: "all .15s",
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Current Brandings
              </Typography>
              <Button variant="outlined" href="/promotions/brands">
                Manage Brandings
              </Button>
            </Stack>

            {loadingBrands ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rounded" height={200} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rounded" height={200} />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, textAlign: "center" }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Logo
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#fff",
                        display: "grid",
                        placeItems: "center",
                        aspectRatio: "1 / 1",
                        maxWidth: 280,
                        mx: "auto",
                        p: 1.5,
                      }}
                    >
                      {logo?.url ? (
                        <Box
                          component="img"
                          src={logo.url}
                          alt={logo.alt || "logo"}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Stack alignItems="center" color="text.secondary">
                          <BrokenImageIcon />
                          <Typography variant="caption">
                            Nothing found
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      alignItems="center"
                      sx={{ mt: 1 }}
                    >
                      <StatusDot active={!!logo?.isActive} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        title={logo?.title || logo?.alt || ""}
                        noWrap
                      >
                        {logo?.title || logo?.alt || "—"}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2, textAlign: "center" }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Banner
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#fff",
                        display: "grid",
                        placeItems: "center",
                        height: 180,
                        px: 1.5,
                      }}
                    >
                      {banner?.url ? (
                        <Box
                          component="img"
                          src={banner.url}
                          alt={banner.alt || "banner"}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Stack alignItems="center" color="text.secondary">
                          <BrokenImageIcon />
                          <Typography variant="caption">
                            Nothing found
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      alignItems="center"
                      sx={{ mt: 1 }}
                    >
                      <StatusDot active={!!banner?.isActive} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        title={banner?.title || banner?.alt || ""}
                        noWrap
                      >
                        {banner?.title || banner?.alt || "—"}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default Promotions;
